import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiVideo, FiTrendingUp, FiAward, FiArrowDownCircle, FiArrowUpCircle,
  FiShield, FiBriefcase, FiCheck, FiX, FiExternalLink,
} from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Pagination from '../../components/Pagination/Pagination';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminAllRequests.module.css';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';

interface ReqRow {
  id: string;
  req_type: 'meeting' | 'invest' | 'partner' | 'deposit' | 'withdrawal' | 'kyc' | 'kyb';
  full_name: string;
  email: string;
  amount: number | null;
  amount_inr: number | null;
  currency: string | null;
  status: string;
  created_at: string;
  user_id: string | null;
  detail: string | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  identity: 'Identity proof', address: 'Address proof', income: 'Income proof',
};

const TYPE_META: Record<string, { label: string; Icon: React.ElementType; simple: boolean; reviewPath?: string }> = {
  meeting: { label: 'Meeting request', Icon: FiVideo, simple: true },
  invest: { label: 'Investment interest', Icon: FiTrendingUp, simple: true },
  partner: { label: 'Partner application', Icon: FiAward, simple: true },
  deposit: { label: 'Deposit', Icon: FiArrowDownCircle, simple: false, reviewPath: '/admin/funds' },
  withdrawal: { label: 'Withdrawal', Icon: FiArrowUpCircle, simple: false, reviewPath: '/admin/funds' },
  kyc: { label: 'KYC verification', Icon: FiShield, simple: false, reviewPath: '/admin/kyc' },
  kyb: { label: 'KYB verification', Icon: FiBriefcase, simple: false, reviewPath: '/admin/kyb' },
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#e8a86a', confirmed: '#4fae7a', approved: '#4fae7a', paid: '#4fae7a',
  processing: '#1d60f6', rejected: '#e2534a',
};

const TYPE_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Meetings', value: 'meeting' },
  { label: 'Investment', value: 'invest' },
  { label: 'Partner', value: 'partner' },
  { label: 'Deposits', value: 'deposit' },
  { label: 'Withdrawals', value: 'withdrawal' },
  { label: 'KYC', value: 'kyc' },
  { label: 'KYB', value: 'kyb' },
];
const PAGE_SIZE = 12;

const AdminAllRequests: React.FC = () => {
  const { currencies: adminCurrencies } = useCurrency();
  const [requests, setRequests] = useState<ReqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [actingId, setActingId] = useState<string | null>(null);
  const [askingContactById, setAskingContactById] = useState<string | null>(null);
  const [contactByDraft, setContactByDraft] = useState('');

  const load = () => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (typeFilter !== 'all') params.type = typeFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    axiosClient.get('/admin/all-requests', { params })
      .then(({ data }) => setRequests(data.requests))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setPage(1); }, [typeFilter, statusFilter]);

  const act = async (row: ReqRow, decision: 'approve' | 'reject', contactBy?: string) => {
    // Approving a meeting needs a "we'll contact you by" date/time — ask for it first
    // instead of silently confirming without one.
    if (row.req_type === 'meeting' && decision === 'approve' && !contactBy) {
      setAskingContactById(row.id);
      setContactByDraft('');
      return;
    }
    setActingId(row.id);
    try {
      if (row.req_type === 'partner') {
        await axiosClient.patch(`/admin/partners/${row.id}/status`, { status: decision === 'approve' ? 'approved' : 'rejected' });
      } else {
        await axiosClient.patch(`/admin/leads/${row.id}/status`, {
          status: decision === 'approve' ? 'confirmed' : 'rejected',
          contactBy: contactBy ? new Date(contactBy).toISOString() : undefined,
        });
      }
      toast.success(decision === 'approve' ? 'Approved.' : 'Rejected.');
      setAskingContactById(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update.');
    } finally {
      setActingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(requests.length / PAGE_SIZE));
  const paged = requests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AdminShell>
      <PageHeader
        title="All Requests"
        subtitle="Everything waiting on a decision — meetings, investments, partner applications, deposits, withdrawals, and KYC/KYB — in one place."
        action={<AdminWalletBadge />}
      />
      <div className={panelStyles.page}>
        <select className={styles.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          {TYPE_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <div className={panelStyles.filters} style={{ marginTop: 12 }}>
          {['pending', 'approved', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              className={`${panelStyles.filterBtn} ${statusFilter === s ? panelStyles.filterBtnActive : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
        {!loading && requests.length === 0 && <div className={panelStyles.empty}>Nothing here for this filter.</div>}

        <div style={{ marginTop: 18 }}>
          {!loading && paged.map((row) => {
            const meta = TYPE_META[row.req_type] || { label: row.req_type, Icon: FiShield, simple: false };
            const isPendingSimple = meta.simple && row.status === 'pending';
            return (
              <div key={`${row.req_type}-${row.id}`} className={styles.card}>
                <div className={styles.cardLeft}>
                  <div className={styles.iconBox}><meta.Icon /></div>
                  <div>
                    <b className={styles.name}>{row.full_name}</b>
                    <div className={styles.subline}>
                      {meta.label}{row.req_type === 'kyc' && row.detail ? ` — ${CATEGORY_LABEL[row.detail] || row.detail}` : ''}{row.amount ? ` · ${adminCurrencies.find((c) => c.code === (row.currency || 'INR'))?.symbol || (row.currency || 'INR') + ' '}${Number(row.amount).toLocaleString()}` : ''} · {new Date(row.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className={styles.cardRight}>
                  <span className={styles.statusPill} style={{ color: STATUS_COLORS[row.status] || 'var(--muted)', borderColor: STATUS_COLORS[row.status] || 'var(--line)' }}>
                    {row.status}
                  </span>
                  {isPendingSimple ? (
                    <>
                      <button className={panelStyles.actionBtn} disabled={actingId === row.id} onClick={() => act(row, 'approve')}>
                        <FiCheck /> Approve
                      </button>
                      <button className={panelStyles.actionBtnReject} disabled={actingId === row.id} onClick={() => act(row, 'reject')}>
                        <FiX /> Reject
                      </button>
                    </>
                  ) : (
                    meta.reviewPath && (
                      <Link to={meta.reviewPath} className={styles.viewLink}>
                        <FiExternalLink /> View &amp; Verify
                      </Link>
                    )
                  )}
                </div>

                {askingContactById === row.id && (
                  <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>We'll contact them by:</span>
                    <input
                      type="datetime-local"
                      value={contactByDraft}
                      onChange={(e) => setContactByDraft(e.target.value)}
                      style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 8, padding: '8px 10px', color: 'var(--cream)', fontSize: 12.5 }}
                    />
                    <button
                      className={panelStyles.actionBtn}
                      disabled={!contactByDraft || actingId === row.id}
                      onClick={() => act(row, 'approve', contactByDraft)}
                    >
                      <FiCheck /> Confirm Meeting
                    </button>
                    <button className={panelStyles.actionBtnReject} onClick={() => setAskingContactById(null)}>Cancel</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!loading && requests.length > PAGE_SIZE && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </AdminShell>
  );
};

export default AdminAllRequests;
